import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core'
import { isString } from 'lodash'
import CheckBoxItem from '../../../molecules/CheckBoxItem'
import SydButton from '../../../commonDesign/Button'
import Loader from '../../../pages/admin/Models/Loader'

const useStyles = makeStyles(() => ({
  modalBody: {
    maxHeight: '60vh',
    overflowY: 'auto',
    padding: '0 0 20px 0'
  },
  checkboxItem: {
    padding: 0,
    marginBottom: '0.5rem'
  },
  buttonsContainer: {
    borderTop: '1px solid var(--color-neutral-200)',
    paddingTop: '20px',
    marginTop: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '1rem'
  }
}))

const mapColumnsToToggleItems = (columns) => {
  return columns.map((column) => {
    const label =
      (isString(column.header) && column.header) ||
      (isString(column.Header) && column.Header) ||
      column.name ||
      column.id
    return {
      id: column.id || column.accessorKey || column.accessor,
      label,
      ...column,
      defaultShown: column.defaultShown ?? true
    }
  })
}

export const ColumnConfigurationContent = ({
  columns = [],
  originalColumns = [],
  onSave,
  onCancel
}) => {
  const classes = useStyles()
  const [columnItems, setColumnItems] = useState([])
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Initialize column items when modal opens or columns change
  useEffect(() => {
    if (columns.length > 0) {
      setColumnItems(mapColumnsToToggleItems(columns))
    }
  }, [columns])

  const handleToggleColumn = useCallback((id, checked) => {
    setColumnItems((prevState) => {
      return prevState.map(item =>
        item.id === id ? { ...item, defaultShown: checked } : item
      )
    })
  }, [])

  const handleSave = useCallback(async () => {
    setIsSubmitting(true)
    try {
      // Call onSave with the column visibility configuration
      if (onSave) {
        await onSave(columnItems)
      }
    } catch (error) {
      console.error('Error saving column configuration:', error)
    } finally {
      setIsSubmitting(false)
    }
  }, [columnItems, onSave])

  const handleCancel = useCallback(() => {
    // Reset to original state
    setColumnItems(mapColumnsToToggleItems(columns))
    if (onCancel) {
      onCancel()
    }
  }, [columns, onCancel])

  const handleResetColumns = useCallback(async () => {
    if (onSave) {
      await onSave(originalColumns)
    }
  }, [onSave, originalColumns])

  return (
    <>
      <div className={classes.modalBody}>
        {columnItems.map(({ id, label, defaultShown }) => (
          <CheckBoxItem
            key={id}
            checked={defaultShown ?? true}
            containerClassName={classes.checkboxItem}
            onChange={(event) => handleToggleColumn(id, event.target.checked)}
          >
            {label}
          </CheckBoxItem>
        ))}
      </div>
      <div className={classes.buttonsContainer}>
        <SydButton onClick={handleResetColumns}>
          <span className='font-size-sm font-bold p-2'>Reset Columns</span>
        </SydButton>
        <SydButton onClick={handleCancel}>
          <span className='font-size-sm font-bold p-2'>Cancel Selections</span>
        </SydButton>
        <SydButton onClick={handleSave} variant='primary'>
          {
            isSubmitting ? (
              <Loader />
            ) : (
              <span className='font-size-sm font-bold p-2'>Apply Changes</span>
            )
          }
        </SydButton>
      </div>
    </>
  )
}

ColumnConfigurationContent.propTypes = {
  /** Array of column configuration objects */
  columns: PropTypes.array,
  /** Array of original column configuration objects */
  originalColumns: PropTypes.array,
  /** Function called when save is clicked with column visibility config */
  onSave: PropTypes.func,
  /** Function called when cancel is clicked */
  onCancel: PropTypes.func
}

ColumnConfigurationContent.defaultProps = {
  columns: [],
  originalColumns: [],
  onSave: undefined,
  onCancel: undefined
}
